import { DownloadButton } from '../DownloadButton';
import { CopyClipboardButton } from '../CopyClipboardButton';
import { PreviewButton } from '../PreviewButton';
import { Pagination } from '../Pagination/Pagination';
import { ChartViewButtons } from '../ChartViewButtons/ChartViewButtons';

type ReportFilterSlotProps<V extends string> = {
  hideOptions?: boolean;
  selectedFilters: JSX.Element;
  isPreview?: boolean;
  setIsPreview?: (isPreview: boolean) => void;
  paginationOptions?: {
    currentPage: number;
    total: number;
    pageSize?: number;
    onChange: (page: number) => void;
  };
  displayOptions: { label: string; icon: (isActive?: boolean) => JSX.Element; value: V }[];
  displayOptionList: V[];
  onSwitchChartViews?: (btn: {
    label: string;
    icon: (i?: boolean) => JSX.Element;
    value: V;
  }) => () => void;
  onDownloadChart: () => void;
  onCopyToClipboard?: () => void;
};

export function ReportFilterSlot<V extends string = string>({
  displayOptions,
  hideOptions,
  displayOptionList,
  paginationOptions,
  onDownloadChart,
  onCopyToClipboard,
  isPreview,
  setIsPreview,
  selectedFilters,
  onSwitchChartViews,
}: ReportFilterSlotProps<V>) {
  return (
    <div data-testid="filter-slot" className="flex flex-col-reverse xl:flex-row justify-end h-16">
      {!isPreview && <div className="w-full inline-flex items-center">{selectedFilters}</div>}
      <div className="w-fit my-2 xl:mb-2 self-end ">
        <div className="flex justify-end">
          {paginationOptions && (
            <div className="w-[28rem] flex justify-end mr-4">
              {!isPreview && <Pagination {...paginationOptions} />}
            </div>
          )}
          {!hideOptions && !isPreview && (
            <ChartViewButtons
              displayOptionList={displayOptionList}
              displayOptions={displayOptions}
              onSwitchChartViews={onSwitchChartViews}
            />
          )}
          {isPreview ? (
            <div className="flex -mt-16 h-12 gap-2 bg-transparent">
              <DownloadButton title="Download Chart" onClick={onDownloadChart} />
              {onCopyToClipboard && (
                <CopyClipboardButton title="Copy to Clipboard" onClick={onCopyToClipboard} />
              )}
            </div>
          ) : (
            <PreviewButton
              title="Export Chart"
              onClick={() => setIsPreview && setIsPreview(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  width: string | number;
  height: string | number;
  children: React.ReactNode;
}

const Modal = ({ isOpen, onClose, width, height, children }: ModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-xl relative overflow-auto"
        style={{ width: width, height: height }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Modal content */}
        <div className="p-6 bg-green m-3">{children}</div>
      </div>
    </div>
  );
};
